import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ShieldCheck, ShieldAlert, ShieldX, Info } from 'lucide-react';
import { computeRisk, levelColor } from '../../lib/ai';
import { useMQTT } from '../../context/MQTTContext';

const SEV_ICON = {
  critical: ShieldX,
  warning:  ShieldAlert,
  caution:  Info,
  info:     Info,
  ok:       ShieldCheck
};
const SEV_FG = {
  critical: '#ef4444',
  warning:  '#f59e0b',
  caution:  '#3b82f6',
  info:     '#3b82f6',
  ok:       '#10b981'
};

export default function AIRiskPanel({ compact = false }) {
  const { sensorData } = useMQTT();
  const { score, level, findings } = computeRisk(sensorData);
  const colors = levelColor(level);

  // SVG circular gauge geometry
  const R = 52;
  const C = 2 * Math.PI * R;
  const offset = C - (score / 100) * C;

  return (
    <motion.div
      layout
      className={`card-surface ring-1 ${colors.ring} p-5 sm:p-6 relative overflow-hidden`}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            `radial-gradient(circle at 90% 0%, ${colors.fg}33 0%, transparent 50%)`
        }}
      />

      <div className="relative flex flex-col sm:flex-row items-center gap-5">
        {/* Gauge */}
        <div className="relative w-[140px] h-[140px] shrink-0">
          <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
            <circle cx="70" cy="70" r={R} stroke="currentColor" strokeOpacity="0.12" strokeWidth="10" fill="none" />
            <motion.circle
              cx="70" cy="70" r={R}
              stroke={colors.fg}
              strokeWidth="10" strokeLinecap="round" fill="none"
              strokeDasharray={C}
              animate={{ strokeDashoffset: offset }}
              initial={false}
              transition={{ type: 'spring', stiffness: 90, damping: 20 }}
              style={{ filter: `drop-shadow(0 0 8px ${colors.fg})` }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-extrabold tabular-nums">{score}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Risk Score</span>
          </div>
        </div>

        {/* Headline */}
        <div className="flex-1 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2"
               style={{ background: colors.bg, color: colors.fg }}>
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">AI Analysis</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight">
            {level === 'safe'     ? 'All systems nominal'    :
             level === 'caution'  ? 'Mild anomalies'         :
             level === 'warning'  ? 'Action recommended'     :
                                    'Critical condition'}
          </h3>
          <p className="text-sm opacity-70 mt-1">
            {findings.length === 1
              ? findings[0].detail
              : `${findings.length} active findings — see details below.`}
          </p>
        </div>
      </div>

      {!compact && (
        <div className="relative mt-5 grid gap-2.5">
          {findings.map((f, i) => {
            const Icon = SEV_ICON[f.severity] || Info;
            const fg = SEV_FG[f.severity] || '#3b82f6';
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="flex gap-3 items-start p-3 rounded-2xl bg-black/[0.03] dark:bg-white/[0.04]"
              >
                <div className="w-9 h-9 shrink-0 rounded-xl grid place-items-center"
                     style={{ background: `${fg}22`, color: fg }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-sm">{f.title}</p>
                  <p className="text-xs opacity-70 mt-0.5">{f.detail}</p>
                  <p className="text-xs font-semibold mt-1.5" style={{ color: fg }}>
                    → {f.action}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
